import { useState, useEffect, useCallback, useRef } from 'react'
import type { Campaign } from '@/features/campaigns'
import type { EnrollmentStep } from '@/features/enrollment'
import { useEnrollmentWizard, useEnrollmentEffects } from '@/features/enrollment/core/hooks'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { ShimmerCard, ShimmerProgressBar, ShimmerNavigation } from '@/shared/components'
import { CampaignFlowFactory } from '@/features/enrollment/core/config/campaignFlows'
import { calculateProgressPercentage, getProgressBarWidth } from '@/shared/utils'

interface ClientEnrollmentWizardProps {
  selectedCampaign: Campaign | null
  onCampaignSelect: (campaign: Campaign | null) => void
}

export default function ClientEnrollmentWizard({ selectedCampaign, onCampaignSelect }: ClientEnrollmentWizardProps) {
  
  // Mobile dropdown state
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false)

  // Use the custom hook for all enrollment wizard logic
  const {
    sessionId,
    enrollmentData,
    currentWizardStepIndex,
    setCurrentWizardStepIndex,
    setEnrollmentData,
    isCompletedMode,
    setIsCompletedMode,
    agentName,
    isLoading,
    setIsLoading,
    isNavigating,
    isCompleting,
    isStartingNew,
    handleNavigate,
    handleSimpleNavigate,
    handleNavigateWithCompletion,
    markStepCompleted,
    updateEnrollmentData,
    updateStepData,
    updatePlatformData,
    generateQRCode,
    sendEmailLink,
    startNewEnrollment,
    completeEnrollment,
    startNextChildFromCurrent,
    resetEnrollment,
    sessionManager,
    navigationManager,
    enrollmentDataRef,
    skipResumeOnceRef,
    movedToPendingRef,
    hiddenTimerRef,
    isRestoringSessionRef,
  } = useEnrollmentWizard(onCampaignSelect)


  // SIMPLIFIED: Handle campaign selection
  const handleCampaignSelect = useCallback(async (campaign: Campaign) => {
    // Clear any existing enrollment data when selecting a new campaign
    if (selectedCampaign && selectedCampaign.id !== campaign.id) {
      resetEnrollment()
      
      // Wait a bit for the reset to complete
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    
    onCampaignSelect(campaign)
    
    updateEnrollmentData('campaign', campaign) // ✅ Store FULL campaign object (has database ID)
    
    
    // ✅ Pass ORIGINAL database ID to session manager
    await sessionManager.setCampaignId(campaign.id) // ✅ Send database ID!
    
    
  }, [selectedCampaign, onCampaignSelect, updateEnrollmentData, resetEnrollment, sessionManager, sessionId])

  // Call handleCampaignSelect when selectedCampaign prop changes
  // ✅ FIXED: Use ref to track if we've already processed this campaign to prevent infinite loop
  const lastProcessedCampaignRef = useRef<string | null>(null)
  
  useEffect(() => {
    if (selectedCampaign && lastProcessedCampaignRef.current !== selectedCampaign.id) {
      
      lastProcessedCampaignRef.current = selectedCampaign.id
      handleCampaignSelect(selectedCampaign)
    }
  }, [selectedCampaign?.id]) // Only depend on campaign ID, not the whole object or handleCampaignSelect

  // Handle back to campaign selection
  const handleBackToSelection = () => {
    onCampaignSelect(null)
  }

  // Handle enrollment completion
  const handleEnrollmentComplete = (data: any) => {
    completeEnrollment()
  }

  // Create a wrapper for handleNavigate that includes steps
  const handleNavigateWithSteps = async (targetIndex: number) => {
    await handleNavigate(targetIndex, steps)
  }

  // Create a wrapper for handleNavigateWithCompletion that includes steps
  const handleNavigateWithCompletionAndSteps = async (targetIndex: number) => {
    await handleNavigateWithCompletion(targetIndex, steps)
  }


  // SIMPLIFIED: Handle resume request from pending applications
  useEffect(() => {
    const handleResumeRequest = async (event: CustomEvent) => {
      const { currentStep, sessionId: eventSessionId } = event.detail || {}
      
      
      // Store the step information for later use when campaign is ready
      if (currentStep !== undefined) {
        localStorage.setItem('pendingResumeStep', currentStep.toString())
      }
      
      // ⭐ IMPORTANT: Call resumeSession to restore all the data
      if (eventSessionId && sessionManager) {
        await sessionManager.resumeSession(
          setEnrollmentData,
          setCurrentWizardStepIndex,
          setIsCompletedMode,
          resetEnrollment,
          false // isCompletedMode
        )
      }
      
      // If we already have a campaign selected, immediately restore the step
      if (selectedCampaign && currentStep !== undefined) {
        setCurrentWizardStepIndex(currentStep)
        localStorage.removeItem('pendingResumeStep')
      }
      
      // Reset the resumeRequested flag after a short delay
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('reset-resume-flag'))
        } catch {}
      }, 1000)
    }

    window.addEventListener('navigate:enrollment', handleResumeRequest as unknown as EventListener)
    
    return () => {
      window.removeEventListener('navigate:enrollment', handleResumeRequest as unknown as EventListener)
    }
  }, [selectedCampaign, sessionManager, setEnrollmentData, setCurrentWizardStepIndex, setIsCompletedMode, resetEnrollment])

  // SIMPLIFIED: Check localStorage on component mount for saved step information
  useEffect(() => {
    const sessionId = localStorage.getItem('enrollmentSessionId')
    const savedStep = localStorage.getItem('enrollmentCurrentStep')
    const pendingResumeStep = localStorage.getItem('pendingResumeStep')
  
    if (sessionId && (savedStep || pendingResumeStep)) {
      const stepIndex = parseInt(savedStep || pendingResumeStep || '0', 10)
   
      // Always set the step if we have a valid step index and campaign is selected
      if (selectedCampaign && stepIndex >= 0) {
        setCurrentWizardStepIndex(stepIndex)
        localStorage.removeItem('enrollmentCurrentStep')
        localStorage.removeItem('enrollmentCampaign')
        localStorage.removeItem('pendingResumeStep')
      }
    }
  }, [selectedCampaign, enrollmentData.selectedCampaign])

  // SIMPLIFIED: Sync selectedCampaign state with enrollment data when it's restored
  useEffect(() => {
    // Only run if we have enrollment data but no selected campaign
    if (enrollmentData.selectedCampaign && !selectedCampaign) {
      
      
      // Check if we have the full campaign object stored
      if (enrollmentData.campaign) {
        onCampaignSelect(enrollmentData.campaign)
      } else {
        // Fallback: create a mock campaign object based on stored data
        const mockCampaign: Campaign = {
          id: enrollmentData.campaignId || 'mock-id',
          name: enrollmentData.selectedCampaign,
          status: 'ACTIVE',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          startDate: new Date().toISOString(),
          endDate: undefined,
          organizationId: '',
          ownerId: ''
        }
        onCampaignSelect(mockCampaign)
      }
    }
  }, [enrollmentData.selectedCampaign, enrollmentData.campaign, enrollmentData.campaignId, selectedCampaign, onCampaignSelect])

  // Separate useEffect for localStorage restoration to avoid loops
  useEffect(() => {
    // Only run if we have no campaign selected and no enrollment data
    if (!selectedCampaign && !enrollmentData.selectedCampaign) {
      const savedCampaignId = localStorage.getItem('enrollmentCampaign')
      const savedCampaignName = localStorage.getItem('enrollmentCampaignName')
      
      if (savedCampaignId && savedCampaignName) {
        const mockCampaign: Campaign = {
          id: savedCampaignId,
          name: savedCampaignName,
          status: 'ACTIVE',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          startDate: new Date().toISOString(),
          endDate: undefined,
          organizationId: '',
          ownerId: ''
        }
        onCampaignSelect(mockCampaign)
      }
    }
  }, [selectedCampaign, enrollmentData.selectedCampaign, onCampaignSelect])

  // Separate useEffect for step restoration - ONLY run once when campaign changes
  const hasRestoredStepRef = useRef(false)
  useEffect(() => {
    // Only run if we have a selected campaign AND haven't restored yet
    if (selectedCampaign && !hasRestoredStepRef.current) {
      const savedStep = localStorage.getItem('enrollmentCurrentStep')
      const pendingResumeStep = localStorage.getItem('pendingResumeStep')
      const savedCampaignId = localStorage.getItem('enrollmentCampaign')

      const stepToRestore = savedStep || pendingResumeStep

      if (stepToRestore) {
        const stepIndex = parseInt(stepToRestore, 10)

        // If we have campaign validation and it matches, or if no campaign validation, restore the step
        if (!savedCampaignId || savedCampaignId === selectedCampaign.id) {
          setCurrentWizardStepIndex(stepIndex)
          hasRestoredStepRef.current = true
        } else {
          // Only reset if campaign truly doesn't match
          if (savedCampaignId && savedCampaignId !== selectedCampaign.id) {
            setCurrentWizardStepIndex(0)
          }
          hasRestoredStepRef.current = true
        }

        // Clean up localStorage
        localStorage.removeItem('enrollmentCurrentStep')
        localStorage.removeItem('enrollmentCampaign')
        localStorage.removeItem('pendingResumeStep')
      } else {
        hasRestoredStepRef.current = true
      }
    }

    // Reset the flag when campaign changes
    if (!selectedCampaign) {
      hasRestoredStepRef.current = false
    }
  }, [selectedCampaign?.id]) // Only depend on campaign ID, not enrollmentData

  // Handle mobile navigation with dropdown close
  const handleMobileNavigation = async (targetIndex: number) => {
    await handleNavigate(targetIndex)
    setIsMobileDropdownOpen(false)
  }

  // Define steps array - dynamically based on selected campaign using the new configuration system
  const getStepsForCampaign = (): EnrollmentStep[] => {
    // If no campaign selected, return empty array
    if (!selectedCampaign) {
      return []
    }


    // Get campaign step configuration
    const campaignSteps = CampaignFlowFactory.createStepsForCampaign(selectedCampaign, {
      enrollmentData,
      updateEnrollmentData,
      updatePlatformData,
      generateQRCode,
      sendEmailLink,
      handleNavigate: handleNavigateWithCompletion,
      handleNavigateWithSteps: handleNavigate,
      sessionId,
      pendingApplications: [],
      showPendingApplications: false,
      setShowPendingApplications: () => {},
      onRefreshPendingApplications: async () => {}
    })


     // ✅ FIXED: Don't store props, just store component reference
     // Props will be passed fresh at render time to ensure components always get current data
     const finalSteps = campaignSteps.map((stepConfig, index) => ({
       id: stepConfig.id,
       title: stepConfig.title,
       description: stepConfig.description,
       component: stepConfig.component // Just the component, no props
       // ❌ Removed componentProps - we'll pass fresh props at render time
     }))
     
    
     
     
     return finalSteps
  }

  const steps = getStepsForCampaign()

  // Use effects hook for all side effects - MUST be called before any conditional returns
  useEnrollmentEffects(
    sessionId,
    currentWizardStepIndex,
    isCompletedMode,
    enrollmentDataRef,
    skipResumeOnceRef,
    movedToPendingRef,
    hiddenTimerRef,
    isRestoringSessionRef,
    setEnrollmentData, // ⭐ Pass the actual setter
    setCurrentWizardStepIndex, // ⭐ Pass the actual setter
    setIsCompletedMode, // ⭐ Pass the actual setter
    resetEnrollment,
    setIsLoading,
    steps
  )

  // Show shimmer effect while loading - this takes priority over "no steps" check
  if (isLoading || (selectedCampaign && steps.length === 0)) {
    return (
      <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 md:px-8 lg:px-8">
        {/* Loading message for resume operations */}
        {isRestoringSessionRef.current && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <div>
              <div className="font-medium text-blue-900">Recovering Session</div>
              <div className="text-sm text-blue-700">Please wait while we restore your previous enrollment session...</div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="h-6 sm:h-7 md:h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded w-24"></div>
        </div>
        <ShimmerProgressBar />
        <ShimmerCard />
        <ShimmerNavigation />
      </div>
    )
  }

  // ✅ Check if campaign is selected but has no steps - ONLY show error after loading is complete
  if (selectedCampaign && steps.length === 0 && !isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 md:px-8 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-bold text-accent-900">
              Client Enrollment
            </h2>
            <p className="text-xs sm:text-sm md:text-sm text-accent-600 mt-1">
              {selectedCampaign.name}
            </p>
          </div>
          <button
            onClick={handleBackToSelection}
            className="btn-outline text-sm px-4 py-2"
          >
            Back to Campaign Selection
          </button>
        </div>
        
        <div className="card p-8 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-accent-900 mb-2">
              No Campaign Steps Found
            </h3>
            <p className="text-accent-600 mb-6">
              The campaign "{selectedCampaign.name}" doesn't have any enrollment steps configured yet. 
              Please contact your administrator to set up the campaign steps.
            </p>
            <button
              onClick={handleBackToSelection}
              className="btn-primary"
            >
              Choose Another Campaign
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 md:px-8 lg:px-8">
      {/* Header - Mobile First Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-bold text-accent-900 truncate">
              Client Enrollment
            </h2>
            {isCompletedMode && (
              <span className="inline-flex items-center text-xs sm:text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full self-start sm:self-center">
                Completed Mode
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm md:text-sm text-accent-600 mt-1">
            Client Enrollment - Step {Math.min(currentWizardStepIndex + 1, steps.length)} of {steps.length}
          </p>
          {sessionId && (
            <p className="text-xs text-accent-500 mt-1 truncate">
              Session: {sessionId.substring(0, 8)}...
            </p>
          )}
        </div>
        
        {/* Desktop Controls */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <div className="text-sm text-accent-500">{steps[currentWizardStepIndex]?.title || 'Loading...'}</div>
          {currentWizardStepIndex > 0 && (
            <button
              onClick={startNewEnrollment}
              className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors flex items-center space-x-1"
              title="Start a new enrollment (moves current to Pending)"
              disabled={isStartingNew}
            >
              {isStartingNew ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : null}
              <span>{isStartingNew ? 'Starting...' : 'New Enrollment'}</span>
            </button>
          )}
        </div>
        
        {/* Mobile/Tablet Controls */}
        <div className="flex md:hidden items-center justify-between gap-2">
          <div className="text-xs sm:text-sm text-accent-500 truncate flex-1">
            {steps[currentWizardStepIndex]?.title || 'Loading...'}
          </div>
          {currentWizardStepIndex > 0 && (
            <button
              onClick={startNewEnrollment}
              className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors flex-shrink-0 flex items-center space-x-1"
              title="Start a new enrollment"
              disabled={isStartingNew}
            >
              {isStartingNew ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : null}
              <span>{isStartingNew ? 'Starting...' : 'New'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <span className="text-xs sm:text-sm md:text-sm font-medium text-accent-700">Progress</span>
          <span className="text-xs sm:text-sm md:text-sm text-accent-500 font-medium">
            {calculateProgressPercentage({
              currentStepIndex: currentWizardStepIndex,
              totalSteps: steps.length,
              completedSteps: enrollmentData.completedSteps || [],
              skippedSteps: enrollmentData.skippedSteps || []
            })}%
          </span>
        </div>
        
        <div className="w-full bg-accent-200 rounded-full h-1 sm:h-2 md:h-2 mb-4 sm:mb-6">
          <div
            className="bg-primary-600 h-1 sm:h-2 md:h-2 rounded-full transition-all duration-300"
            style={{ width: getProgressBarWidth({
              currentStepIndex: currentWizardStepIndex,
              totalSteps: steps.length,
              completedSteps: enrollmentData.completedSteps || [],
              skippedSteps: enrollmentData.skippedSteps || []
            }) }}
          ></div>
        </div>
        
        <div className="space-y-3 sm:space-y-0">
          {/* Mobile: Dropdown Navigation */}
          <div className="block md:hidden">
            <button
              onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
              className="w-full flex items-center justify-between p-3 bg-white border border-accent-200 rounded-lg hover:bg-accent-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentWizardStepIndex < steps.length 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-accent-200 text-accent-600'
                }`}>
                  {currentWizardStepIndex + 1}
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-accent-900">
                    {steps[currentWizardStepIndex]?.title || 'Loading...'}
                  </div>
                  <div className="text-xs text-accent-500">
                    {steps[currentWizardStepIndex]?.description || 'Please wait...'}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">
                {isMobileDropdownOpen ? (
                  <ChevronUp className="w-5 h-5 text-accent-500 transition-transform duration-200" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-accent-500 transition-transform duration-200" />
                )}
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isMobileDropdownOpen 
                ? 'max-h-96 opacity-100 mt-2' 
                : 'max-h-0 opacity-0'
            }`}>
              <div className="bg-white border border-accent-200 rounded-lg shadow-sm">
                <div className="max-h-80 overflow-y-auto">
                  {steps.map((step, index) => {
                    const { containerClasses, circleClasses, textClasses, stepNumber } = 
                      navigationManager.getStepStylingClasses(index, enrollmentData)
                    const isCurrentStep = index === currentWizardStepIndex
                    const isClickable = navigationManager.isStepClickable(index, enrollmentData)
                    
                    return (
                      <button
                        key={step.id}
                        onClick={() => isClickable && handleMobileNavigation(index)}
                        disabled={!isClickable}
                        className={`w-full flex items-center gap-3 p-3 text-left transition-all duration-200 ${
                          isCurrentStep 
                            ? 'bg-primary-50 border-l-4 border-primary-500' 
                            : isClickable 
                              ? 'hover:bg-accent-50' 
                              : 'opacity-50 cursor-not-allowed'
                        } ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                      >
                        <div className={`flex-shrink-0 ${circleClasses}`}>
                          {stepNumber}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`block text-sm font-medium ${textClasses}`}>
                            {step.title}
                          </span>
                          <span className="text-xs text-accent-500 truncate">
                            {step.description}
                          </span>
                        </div>
                        {isCurrentStep && (
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          
          {/* Desktop: Horizontal Scroll */}
          <div className="hidden md:flex justify-between overflow-x-auto pb-2">
            {steps.map((step, index) => {
              const { containerClasses, circleClasses, textClasses, stepNumber } = 
                navigationManager.getStepStylingClasses(index, enrollmentData)
              
              return (
                <div 
                  key={step.id} 
                  className={`flex flex-col items-center min-w-0 flex-1 max-w-16 md:max-w-20 lg:max-w-24 ${containerClasses}`}
                  onClick={async () => {
                    if (navigationManager.isStepClickable(index, enrollmentData)) {
                      await handleNavigate(index)
                    }
                  }}
                >
                  <div className={`mb-2 ${circleClasses}`}>
                    {stepNumber}
                  </div>
                  <span className={`text-center text-xs md:text-xs lg:text-sm leading-tight ${textClasses}`}>
                    {step.title}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Current Step Content */}
      <div className="w-full">
      <div 
          key={`step-${currentWizardStepIndex}`}
          className="animate-in slide-in-from-top-4 duration-500 ease-out"
        >
          {(() => {
           
           
            const CurrentComponent = steps[currentWizardStepIndex]?.component as unknown as React.ComponentType<any>
            
            if (!CurrentComponent) {
              console.error('❌ No component found for step:', currentWizardStepIndex)
              return <div>Loading...</div>
            }
            
            // ✅ CRITICAL: Pass FRESH props with current enrollmentData
            const freshProps = {
              enrollmentData, // ✅ Use current enrollmentData from state
              updateEnrollmentData,
              updateStepData,
              updatePlatformData,
              generateQRCode,
              sendEmailLink,
              handleNavigate: handleNavigateWithCompletionAndSteps,
              handleSimpleNavigate,
              handleNavigateWithCompletion: handleNavigateWithCompletionAndSteps,
              sessionId,
              currentStepIndex: currentWizardStepIndex, // ✅ Pass current step index
              campaignId: selectedCampaign?.id || enrollmentData.campaignId, // ✅ Pass campaign ID
              pendingApplications: [],
              showPendingApplications: false,
              setShowPendingApplications: () => {},
              onRefreshPendingApplications: async () => {},
              onComplete: completeEnrollment,
              onStartNextChild: startNextChildFromCurrent,
              isCompletedMode,
              isNavigating,
              isCompleting,
              onCampaignSelect,
              agentName,
              campaign: selectedCampaign // ✅ Pass campaign so components can access minAge/maxAge settings
            }
            
            // Render the component with fresh props
            try {
              return <CurrentComponent {...freshProps} />
            } catch (error) {
              console.error('❌ Error rendering component:', error)
              return <div>Error rendering component: {error instanceof Error ? error.message : 'Unknown error'}</div>
            }
          })()}
        </div>
      </div>
    </div>
  )
}